import React, { useMemo } from 'react';
import type { KoobSelectValue } from './KoobInputSelect';
import KoobInputTree, { type KoobTreeItem } from './KoobInputTree';

export interface KoobCountryRegionOption {
  value: KoobSelectValue;
  label: string;
}

export interface KoobCountryOption {
  value: KoobSelectValue;
  label: string;
  /** ISO alpha-2 code — renders a flag-icon */
  code?: string;
  regions?: KoobCountryRegionOption[];
}

export interface KoobCountriesValue {
  countries: KoobSelectValue[];
  regions: KoobSelectValue[];
}

type KoobInputCountriesProps = {
  countries: KoobCountryOption[];
  /** Selecting a country selects all its regions; both lists move together. */
  value: KoobCountriesValue;
  onValueChange: (value: KoobCountriesValue) => void;
  searchable?: boolean;
  placeholder?: string;
  loading?: boolean;
  disabled?: boolean;
  error?: boolean;
  label?: React.ReactNode;
  required?: boolean;
  /** Trailing content on a country row (badges, actions). */
  renderCountryExtra?: (country: KoobCountryOption) => React.ReactNode;
  'data-testid'?: string;
  /** Not part of the API (see packages/ui/README.md) — stripped at runtime */
  className?: never;
};

/** KoobInputTree in checkbox mode, speaking `{ countries, regions }`. */
const KoobInputCountries = React.forwardRef<
  HTMLDivElement,
  KoobInputCountriesProps
>(
  (
    {
      countries,
      value,
      onValueChange,
      placeholder = 'Search countries...',
      renderCountryExtra,
      'data-testid': dataCy = 'countries',
      className,
      ...rest
    },
    ref,
  ) => {
    const items = useMemo<KoobTreeItem[]>(
      () =>
        (countries ?? []).map(({ regions, ...country }) => ({
          ...country,
          children: regions,
        })),
      [countries],
    );

    // Tree leaves = regions + childless countries
    const selected = useMemo(() => {
      const regions = value?.regions ?? [];
      const childless = (countries ?? [])
        .filter(
          (c) => !c.regions?.length && value?.countries?.includes(c.value),
        )
        .map((c) => c.value);
      return [...regions, ...childless];
    }, [countries, value]);

    const handleChange = (leaves: KoobSelectValue[]) => {
      const regionValues = new Set(
        (countries ?? []).flatMap((c) => (c.regions ?? []).map((r) => r.value)),
      );
      onValueChange({
        regions: leaves.filter((v) => regionValues.has(v)),
        countries: (countries ?? [])
          .filter((c) =>
            c.regions?.length
              ? c.regions.every((r) => leaves.includes(r.value))
              : leaves.includes(c.value),
          )
          .map((c) => c.value),
      });
    };

    const countryByValue = useMemo(
      () => new Map((countries ?? []).map((c) => [c.value, c])),
      [countries],
    );

    return (
      <KoobInputTree
        ref={ref}
        items={items}
        value={selected}
        onValueChange={handleChange}
        placeholder={placeholder}
        childrenLabel={(n) => `${n} region${n > 1 ? 's' : ''}`}
        emptyTitle="No countries available"
        emptySearchTitle="No countries found"
        emptyIcon={<i className="fa-regular fa-globe text-2xl" />}
        renderTrailing={
          renderCountryExtra
            ? (item, parent) => {
                const country = !parent && countryByValue.get(item.value);
                return country ? renderCountryExtra(country) : null;
              }
            : undefined
        }
        data-testid={dataCy}
        {...rest}
      />
    );
  },
);

KoobInputCountries.displayName = 'KoobInputCountries';

export default KoobInputCountries;
